import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { AuthService } from 'auth';
import { TranslateService } from 'i18n';
import { Subscription } from 'rxjs';

interface Translations {
  title: string;
  intro: string;
  firstNamePlaceholder: string;
  lastNamePlaceholder: string;
  emailPlaceholder: string;
  subjectPlaceholder: string;
  messagePlaceholder: string;
  sendButton: string;
  sendingButton: string;
  successTitle: string;
  successMessage: string;
}

const TRANSLATIONS: Record<string, Translations> = {
  de: {
    title: 'Support',
    intro: 'Senden Sie uns eine Nachricht und wir melden uns bei Ihnen.',
    firstNamePlaceholder: 'Vorname',
    lastNamePlaceholder: 'Nachname',
    emailPlaceholder: 'E-Mail',
    subjectPlaceholder: 'Betreff',
    messagePlaceholder: 'Ihre Nachricht...',
    sendButton: 'Nachricht senden',
    sendingButton: 'Wird gesendet...',
    successTitle: 'Vielen Dank! Ihre Nachricht wurde gesendet.',
    successMessage: 'Wir melden uns schnellstmöglich bei Ihnen.'
  },
  en: {
    title: 'Support',
    intro: 'Send us a message and we will get back to you.',
    firstNamePlaceholder: 'First name',
    lastNamePlaceholder: 'Last name',
    emailPlaceholder: 'Email',
    subjectPlaceholder: 'Subject',
    messagePlaceholder: 'Your message...',
    sendButton: 'Send message',
    sendingButton: 'Sending...',
    successTitle: 'Thank you! Your message has been sent.',
    successMessage: 'We will get back to you as soon as possible.'
  }
};

@Injectable({
  providedIn: 'root'
})
export class ZammadChatService implements OnDestroy {
  private userSubscription: Subscription | null = null;
  private supportLink: HTMLElement | null = null;
  private chatWindow: HTMLElement | null = null;
  private currentEmail: string | null = null;
  private currentFirstName: string | null = null;
  private currentLastName: string | null = null;
  private currentLang: string = 'en';
  private isOpen = false;
  private retryCount = 0;
  private maxRetries = 10;

  constructor(
    private authService: AuthService,
    private translateService: TranslateService,
    private http: HttpClient
  ) {}

  initialize(): void {
    this.userSubscription = this.authService.getUser$()
      .subscribe(user => {
        if (user) {
          this.currentEmail = user.email;
          this.currentFirstName = user.first_name || user.username;
          this.currentLastName = user.last_name || '';
          this.currentLang = user.language || this.translateService.getActiveLang() || 'en';
          this.createWidget();
        } else {
          this.removeWidget();
        }
      });
  }

  private get t(): Translations {
    return TRANSLATIONS[this.currentLang] || TRANSLATIONS['en'];
  }

  private createWidget(): void {
    if (this.supportLink) return;

    this.addStyles();
    this.createSidebarLink();
    this.createWindow();
  }

  private addStyles(): void {
    if (document.getElementById('support-widget-styles')) return;

    const styles = document.createElement('style');
    styles.id = 'support-widget-styles';
    styles.textContent = `
      .support-sidebar-link {
        margin-left: 8px;
        font-size: 0.9rem;
        color: var(--cui-link-color, #6c757d);
        transition: color 0.2s ease-in-out;
        cursor: pointer;
        display: flex;
        align-items: center;
      }
      .support-sidebar-link svg {
        vertical-align: middle;
        margin-right: 4px;
        width: 1rem;
        height: 1rem;
        fill: currentColor;
      }
      .support-sidebar-link a {
        vertical-align: middle;
        text-decoration: none;
        color: inherit;
      }
      .support-widget-window {
        position: fixed;
        bottom: 90px;
        left: 276px;
        width: 380px;
        background: #1a1a1a;
        border-radius: 12px;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
        z-index: 9998;
        display: none;
        flex-direction: column;
        overflow: hidden;
        border: 1px solid #2d2d2d;
      }
      .support-widget-window.open {
        display: flex;
      }
      .support-widget-header {
        background: #00E5FF;
        color: #000;
        padding: 16px;
        font-weight: bold;
        font-size: 16px;
        display: flex;
        justify-content: space-between;
        align-items: center;
      }
      .support-widget-close {
        background: none;
        border: none;
        font-size: 24px;
        cursor: pointer;
        color: #000;
        padding: 0;
        line-height: 1;
      }
      .support-widget-body {
        padding: 20px;
        background: #242424;
      }
      .support-widget-body p {
        color: #ccc;
        margin: 0 0 16px 0;
        font-size: 14px;
      }
      .support-widget-form {
        display: flex;
        flex-direction: column;
        gap: 12px;
      }
      .support-widget-form input,
      .support-widget-form textarea {
        background: #1a1a1a;
        border: 1px solid #3d3d3d;
        border-radius: 8px;
        padding: 12px;
        color: #fff;
        font-size: 14px;
        font-family: inherit;
      }
      .support-widget-form input:focus,
      .support-widget-form textarea:focus {
        outline: none;
        border-color: #00E5FF;
      }
      .support-widget-form textarea {
        min-height: 120px;
        resize: vertical;
      }
      .support-widget-form button {
        background: #00E5FF;
        color: #000;
        border: none;
        border-radius: 8px;
        padding: 12px 24px;
        font-size: 14px;
        font-weight: bold;
        cursor: pointer;
        transition: background 0.2s;
      }
      .support-widget-form button:hover {
        background: #00d4e8;
      }
      .support-widget-form button:disabled {
        background: #555;
        cursor: not-allowed;
      }
      .support-widget-success {
        text-align: center;
        padding: 20px;
        color: #00FF9F;
      }
      .support-widget-success svg {
        width: 48px;
        height: 48px;
        fill: #00FF9F;
        margin-bottom: 12px;
      }
      @media (max-width: 991.98px) {
        .support-widget-window {
          left: 20px;
          right: 20px;
          width: auto;
          bottom: 20px;
        }
      }
      @media (max-width: 420px) {
        .support-widget-window {
          left: 10px;
          right: 10px;
          bottom: 10px;
        }
      }
    `;
    document.head.appendChild(styles);
  }

  private createSidebarLink(): void {
    // Prevent duplicate links (race condition guard)
    if (document.querySelector('c-sidebar-footer .support-sidebar-link')) {
      this.retryCount = 0;
      return;
    }

    // Find the sidebar footer with the policy link
    const policyDiv = document.querySelector('c-sidebar-footer .policy');
    if (!policyDiv) {
      // Retry if sidebar not yet rendered
      if (this.retryCount < this.maxRetries) {
        this.retryCount++;
        setTimeout(() => this.createSidebarLink(), 200);
      }
      return;
    }

    this.retryCount = 0;

    // Create support link element
    this.supportLink = document.createElement('div');
    this.supportLink.className = 'support-sidebar-link';
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    const svgPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    svgPath.setAttribute('d', 'M11.5 2C6.81 2 3 5.81 3 10.5S6.81 19 11.5 19h.5v3c4.86-2.34 8-7 8-11.5C20 5.81 16.19 2 11.5 2zm1 14.5h-2v-2h2v2zm0-3.5h-2c0-3.25 3-3 3-5 0-1.1-.9-2-2-2s-2 .9-2 2h-2c0-2.21 1.79-4 4-4s4 1.79 4 4c0 2.5-3 2.75-3 5z');
    svg.appendChild(svgPath);
    const linkLabel = document.createElement('a');
    linkLabel.textContent = this.t.title;
    this.supportLink.appendChild(svg);
    this.supportLink.appendChild(linkLabel);
    this.supportLink.onclick = (e) => {
      e.stopPropagation();
      this.toggle();
    };

    // Insert before policy link
    policyDiv.parentNode?.insertBefore(this.supportLink, policyDiv);
  }

  private createWindow(): void {
    this.chatWindow = document.createElement('div');
    this.chatWindow.className = 'support-widget-window';
    // Header
    const header = document.createElement('div');
    header.className = 'support-widget-header';
    const headerTitle = document.createElement('span');
    headerTitle.textContent = this.t.title;
    const closeBtn = document.createElement('button');
    closeBtn.className = 'support-widget-close';
    closeBtn.textContent = '\u00d7';
    header.appendChild(headerTitle);
    header.appendChild(closeBtn);

    // Body
    const body = document.createElement('div');
    body.className = 'support-widget-body';
    const intro = document.createElement('p');
    intro.textContent = this.t.intro;

    // Form
    const form = document.createElement('form');
    form.className = 'support-widget-form';

    const firstNameInput = document.createElement('input');
    firstNameInput.type = 'text';
    firstNameInput.name = 'first_name';
    firstNameInput.placeholder = this.t.firstNamePlaceholder;
    firstNameInput.value = this.currentFirstName || '';
    firstNameInput.required = true;

    const lastNameInput = document.createElement('input');
    lastNameInput.type = 'text';
    lastNameInput.name = 'last_name';
    lastNameInput.placeholder = this.t.lastNamePlaceholder;
    lastNameInput.value = this.currentLastName || '';

    const emailInput = document.createElement('input');
    emailInput.type = 'email';
    emailInput.name = 'email';
    emailInput.placeholder = this.t.emailPlaceholder;
    emailInput.value = this.currentEmail || '';
    emailInput.required = true;

    const subjectInput = document.createElement('input');
    subjectInput.type = 'text';
    subjectInput.name = 'subject';
    subjectInput.placeholder = this.t.subjectPlaceholder;
    subjectInput.required = true;

    const messageTextarea = document.createElement('textarea');
    messageTextarea.name = 'message';
    messageTextarea.placeholder = this.t.messagePlaceholder;
    messageTextarea.required = true;

    const submitBtn = document.createElement('button');
    submitBtn.type = 'submit';
    submitBtn.textContent = this.t.sendButton;

    form.appendChild(firstNameInput);
    form.appendChild(lastNameInput);
    form.appendChild(emailInput);
    form.appendChild(subjectInput);
    form.appendChild(messageTextarea);
    form.appendChild(submitBtn);

    body.appendChild(intro);
    body.appendChild(form);
    this.chatWindow.appendChild(header);
    this.chatWindow.appendChild(body);

    closeBtn.addEventListener('click', () => this.toggle());
    form.addEventListener('submit', (e) => this.handleSubmit(e));

    document.body.appendChild(this.chatWindow);
  }

  private handleSubmit(e: Event): void {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const submitBtn = form.querySelector('button[type="submit"]') as HTMLButtonElement;
    const formData = new FormData(form);

    const first_name = formData.get('first_name') as string;
    const last_name = formData.get('last_name') as string;
    const email = formData.get('email') as string;
    const subject = formData.get('subject') as string;
    const message = formData.get('message') as string;

    submitBtn.disabled = true;
    submitBtn.textContent = this.t.sendingButton;

    // Send via backend API
    this.http.post<{ success: boolean }>('/support/ticket', {
      first_name,
      last_name,
      email,
      subject,
      message
    }).subscribe({
      next: () => {
        this.showSuccess();
      },
      error: () => {
        // Fallback to email client
        const fullName = [first_name, last_name].filter(Boolean).join(' ');
        const subjectEncoded = encodeURIComponent(subject);
        const body = encodeURIComponent(`${message}\n\n---\nName: ${fullName}\nEmail: ${email}`);
        window.location.href = `mailto:support@fac3less.app?subject=${subjectEncoded}&body=${body}`;
        this.showSuccess();
      }
    });
  }

  private showSuccess(): void {
    const body = this.chatWindow?.querySelector('.support-widget-body');
    if (body) {
      body.textContent = '';
      const successDiv = document.createElement('div');
      successDiv.className = 'support-widget-success';
      const successSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      successSvg.setAttribute('viewBox', '0 0 24 24');
      const successPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      successPath.setAttribute('d', 'M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z');
      successSvg.appendChild(successPath);
      const successP1 = document.createElement('p');
      successP1.textContent = this.t.successTitle;
      const successP2 = document.createElement('p');
      successP2.style.color = '#888';
      successP2.style.fontSize = '13px';
      successP2.textContent = this.t.successMessage;
      successDiv.appendChild(successSvg);
      successDiv.appendChild(successP1);
      successDiv.appendChild(successP2);
      body.appendChild(successDiv);
    }
    setTimeout(() => this.toggle(), 3000);
  }

  private toggle(): void {
    this.isOpen = !this.isOpen;
    this.chatWindow?.classList.toggle('open', this.isOpen);

    // Reset form when closing
    if (!this.isOpen) {
      setTimeout(() => {
        const body = this.chatWindow?.querySelector('.support-widget-body');
        if (body && body.querySelector('.support-widget-success')) {
          this.chatWindow?.remove();
          this.chatWindow = null;
          this.createWindow();
        }
      }, 300);
    }
  }

  private removeWidget(): void {
    this.supportLink?.remove();
    this.chatWindow?.remove();
    this.supportLink = null;
    this.chatWindow = null;
    this.currentEmail = null;
    this.currentFirstName = null;
    this.currentLastName = null;
    this.isOpen = false;
    this.retryCount = 0;
  }

  ngOnDestroy(): void {
    this.userSubscription?.unsubscribe();
    this.removeWidget();
  }
}
